import type { Company } from '@gat-ship/types'

/**
 * Functional core — entity-level transforms on a single `Company`.
 *
 * Pure builders and state transitions: a Company (or raw fields) in, a
 * brand-new Company out, with the capability flags that encode the domain
 * rules for each lifecycle change. Shared by both the optimistic command path
 * (`company-commands`) and the SignalR sync path, so the meaning of "create",
 * "deactivate", etc. lives in exactly one place.
 *
 * Consume namespaced, mirroring `company-collection`:
 * `import * as CompanyEntity from './company-entity'`.
 */

/**
 * Build a Company from raw fields, defaulting the capability flags for a new
 * active company.
 */
export function create(
  code: string,
  name: string,
  description?: string,
  isInactive = false,
  canChangeDetails = true,
  canDeactivate = true,
  canReactivate = false
): Company {
  return {
    code,
    name,
    description,
    isInactive,
    canChangeDetails,
    canDeactivate,
    canReactivate
  }
}

/** Transition a Company to inactive, flipping its capability flags. */
export function deactivate(company: Company): Company {
  return {
    ...company,
    isInactive: true,
    canDeactivate: false,
    canReactivate: true,
    canChangeDetails: false
  }
}

/** Transition a Company back to active, flipping its capability flags. */
export function activate(company: Company): Company {
  return {
    ...company,
    isInactive: false,
    canDeactivate: true,
    canReactivate: false,
    canChangeDetails: true
  }
}

/** Return a copy of `company` with updated name and description. */
export function updateDetails(
  company: Company,
  name: string,
  description?: string
): Company {
  return {
    ...company,
    name,
    description
  }
}
